import type { CurrentApplicationUserRolesResult } from "@/types/generated/UsedGeneratedTypes";
import type { LocalNode } from "@/types/front/LocalNode";

/**
 * Nœud enrichi avec les droits CRUD calculés à partir des autorisations de l'application.
 */
export interface AuthorizedNode extends Omit<LocalNode, "children"> {
  children: AuthorizedNode[];
  autorizations: Record<string, boolean> | undefined;
  canUpload: boolean;
  canRead: boolean;
  canDownload: boolean;
  canDelete: boolean;
  canShow: boolean;
  isAdmin: boolean;
}

/**
 * Calcule les droits CRUD d'un nœud (référence ou donnée) à partir des autorisations de l'application.
 * L'absence d'autorisation explicite est interprétée comme un accès admin total.
 *
 * @param reference  Nœud source
 * @param authorizations  Map d'autorisations de l'application (application.authorizations)
 */
export function computeAuthorizations(
  reference: LocalNode,
  authorizations: Record<string, Record<string, boolean>>,
): AuthorizedNode {
  const auth = authorizations[reference.id] || {};
  const isAdmin = !authorizations[reference.id];
  const children = (reference.children || []).map(child =>
    computeAuthorizations(child, authorizations),
  );
  return {
    ...reference,
    children,
    autorizations: authorizations[reference.id],
    canUpload: isAdmin || !!auth["UPLOAD"],
    canRead: isAdmin || !!auth["DOWNLOAD"],
    canDownload: isAdmin || !!auth["DOWNLOAD"],
    canDelete: isAdmin || !!auth["DELETE"],
    isAdmin,
    canShow: isAdmin || !!auth["ANY"],
  };
}

/**
 * Recherche récursive d'un nœud par son id dans une arborescence.
 *
 * @param elements  Tableau de nœuds racines
 * @param id        Identifiant recherché
 * @returns Le nœud trouvé, ou undefined
 */
export function findNodeById(
  elements: LocalNode[],
  id: string,
): LocalNode | undefined {
  if (!elements || elements.length === 0) return undefined;
  for (const ref of elements) {
    if (ref.id === id) return ref;
    const found = findNodeById(ref.children, id);
    if (found) return found;
  }
  return undefined;
}

/**
 * Détermine si un type de donnée a une stratégie de versioning OA_VERSIONING configurée.
 *
 * @param dataDescription  application.configuration.dataDescription
 * @param dataId           Identifiant du type de donnée
 */
export function hasVersioningStrategy(
  dataDescription: Record<string, { submission?: { strategy?: string } } | undefined>,
  dataId: string,
): boolean {
  return dataDescription?.[dataId]?.submission?.strategy === "OA_VERSIONING";
}

/**
 * Calcule les drapeaux de permissions de l'utilisateur courant sur l'application.
 *
 * @param rolesResult  currentApplicationUserRolesResult de l'application chargée
 */
export function computeUserPermissions(rolesResult?: CurrentApplicationUserRolesResult): {
  canCreateApplication: boolean;
  canManagerApplication: boolean;
  canManagerUser: boolean;
} {
  return {
    canCreateApplication: !!(rolesResult?.isApplicationCreator || rolesResult?.isOpenAdomAdmin),
    canManagerApplication: !!(rolesResult?.applicationRoles?.includes("applicationManager")),
    canManagerUser: !!(rolesResult?.applicationRoles?.includes("userManager")),
  };
}
